import Konva from "konva";
import { Exposure } from "./filters/exposure";
import { Highlights } from "./filters/highlights";
import { Sepia } from "./filters/sepia";
import { Shadows } from "./filters/shadows";
import { Sharpen } from "./filters/sharpen";
import { Vignette } from "./filters/vignette";

export interface AdjustmentSettings {
  brightness: number;
  contrast: number;
  saturation: number;
  sharpness: number;
  hue: number;
  exposure: number;
  highlights: number;
  shadows: number;
  noise: number;
  sepia: number;
  pixelate: number;
  vignetteSize: number;
  vignetteStrength: number; // -100 to 100: -100=white, 100=black
}

export const defaultSettings: AdjustmentSettings = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  sharpness: 0,
  hue: 0,
  exposure: 0,
  highlights: 0,
  shadows: 0,
  noise: 0,
  sepia: 0,
  pixelate: 0,
  vignetteSize: 0,
  vignetteStrength: 40, // 0 = no effect
};

// Organized adjustment categories
export const getAdjustmentCategories = (t: (key: string) => string) => ({
  basic: {
    name: t("studio.effects.categories.basic"),
    adjustments: [
      {
        key: "brightness" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.brightness"),
        min: -100,
        max: 100,
        step: 1,
      },
      {
        key: "contrast" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.contrast"),
        min: -100,
        max: 100,
        step: 1,
      },
      {
        key: "saturation" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.saturation"),
        min: -100,
        max: 100,
        step: 1,
      },
      {
        key: "sharpness" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.sharpness"),
        min: -100,
        max: 100,
        step: 1,
      },
    ],
  },
  tone: {
    name: t("studio.effects.categories.lighting"),
    adjustments: [
      {
        key: "exposure" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.exposure"),
        min: -100,
        max: 100,
        step: 1,
      },
      {
        key: "highlights" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.highlights"),
        min: -100,
        max: 100,
        step: 1,
      },
      {
        key: "shadows" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.shadows"),
        min: -100,
        max: 100,
        step: 1,
      },
    ],
  },
  vignette: {
    name: t("studio.effects.categories.vignette"),
    adjustments: [
      {
        key: "vignetteSize" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.size"),
        min: 0,
        max: 100,
        step: 1,
      },
      {
        key: "vignetteStrength" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.intensity"),
        min: -100,
        max: 100,
        step: 1,
      },
    ],
  },
  effects: {
    name: t("studio.effects.categories.other"),
    adjustments: [
      {
        key: "sepia" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.sepia"),
        min: 0,
        max: 100,
        step: 1,
      },
      {
        key: "hue" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.hue"),
        min: -180,
        max: 180,
        step: 1,
      },
      {
        key: "pixelate" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.pixelate"),
        min: 0,
        max: 100,
        step: 1,
      },
      {
        key: "noise" as keyof AdjustmentSettings,
        label: t("studio.effects.adjustments.noise"),
        min: -100,
        max: 100,
        step: 1,
      },
    ],
  },
});

// Legacy export for backwards compatibility
export const adjustmentCategories = getAdjustmentCategories((key) => key);

// Utility functions to be used by parent component
export const createKonvaFilters = (
  imageRef: any,
  partialSettings: Partial<AdjustmentSettings>
) => {
  if (!imageRef.current) return [];
  const filters = [];

  const settings = {
    ...defaultSettings,
    ...partialSettings,
  };

  // If any of the filters is not 0
  if (Object.values(settings).some((value) => value !== 0)) {
    // Basic Konva filters
    filters.push(Konva.Filters.Brighten);
    filters.push(Konva.Filters.Contrast);
    filters.push(Konva.Filters.HSL);

    // Add blur filter if sharpness is negative
    if (settings.sharpness < 0) {
      filters.push(Konva.Filters.Blur);
    }

    // Add noise filter if noise value is set
    if (settings.noise !== 0) {
      filters.push(Konva.Filters.Noise);
    }

    // Add sepia filter if sepia value is set (using custom filter instead of Konva's)
    if (settings.sepia !== 0) {
      filters.push(Sepia(settings.sepia));
    }

    // Remove threshold filter

    // Add pixelate filter if pixelate value is set
    if (settings.pixelate !== 0) {
      filters.push(Konva.Filters.Pixelate);
    }

    // Custom filters
    if (settings.sharpness > 0) {
      filters.push(Sharpen(settings.sharpness / 50));
    }

    if (settings.exposure !== 0) {
      filters.push(Exposure(settings.exposure / 100));
    }

    if (settings.highlights !== 0) {
      filters.push(Highlights(settings.highlights));
    }

    if (settings.shadows !== 0) {
      filters.push(Shadows(settings.shadows));
    }

    // Add vignette filter if any vignette setting is set
    if (settings.vignetteSize > 0 && settings.vignetteStrength !== 0) {
      filters.push(Vignette(settings.vignetteSize, settings.vignetteStrength));
    }
  }

  // Set filter properties on the image reference
  imageRef.current.brightness = () => settings.brightness / 200;
  imageRef.current.contrast = () => settings.contrast / 4;
  imageRef.current.saturation = () =>
    settings.saturation / (settings.saturation > 0 ? 100 : 20);
  imageRef.current.hue = () => settings.hue;
  imageRef.current.blurRadius = () => Math.max(0, -settings.sharpness / 10);
  imageRef.current.noise = () => Math.abs(settings.noise) / 400;
  imageRef.current.pixelSize = () => Math.max(1, settings.pixelate / 5);

  return filters;
};
